import React, { useCallback, useEffect, useState } from 'react'
import {
  AlertTriangle,
  Ban,
  Gavel,
  Lock,
  Plus,
  Scale,
  Send,
  ShieldCheck,
  Users,
} from 'lucide-react'
import { caseRoles, createCaseRole, previewCaseRole, updateCaseRole } from './adminApi'
import { NOT_ENTITLED } from '../../cases/api'
import RoleMigrationPrompt from './RoleMigrationPrompt'
import {
  Button,
  Chip,
  Field,
  Modal,
  Notice,
  Panel,
  Refusal,
  Spinner,
} from '../../cases/ui'
import { pluralise } from '../../cases/format'

// ======================================================================
// THE CASE ROLE BUILDER
// ======================================================================
//
// A tenant composes its own case roles by ticking capabilities. Nothing
// in this file describes what a capability does: every name, every
// description and every warning comes from the server, generated from
// acl.CAPABILITIES and acl._CAPABILITY_VOCABULARY. A capability with no
// description fails the backend at import rather than rendering here as
// a blank row.
//
// ======================================================================
// A ROLE'S DEFINITION IS WHAT IT CANNOT DO AS MUCH AS WHAT IT CAN
// ======================================================================
//
// So the builder shows the WHOLE vocabulary with the selections marked,
// never a list of the chosen ones. Somebody reading a role sees the
// absences — no record_finding, no manage_parties — which is the half
// that stops a role being built with more power than was meant. The
// blocked capabilities are shown too, disabled, with the reason: a list
// that quietly omitted two would be a list nobody could reason about.
//
// ======================================================================
// THE PROTECTED FOUR
// ======================================================================
//
// case_owner, investigator, decision_maker and appeal_manager are
// renameable and their capabilities are read-only, with the reason
// stated on the row. They carry the separation the module rests on —
// whoever investigates does not decide, whoever administers does not
// decide, whoever decides does not author the record they decide from —
// and appeal_manager is defined server-side as decision_maker plus two,
// so leaving it editable would break that union by data.

const CONSEQUENCE = {
  adjudicates: {
    Icon: Gavel,
    title: 'This makes the role a deciding one',
    body:
      'Anybody holding it cannot also investigate the same case, or hold '
      + 'another deciding role on it. That is enforced when the role is '
      + 'granted and by the database — whoever investigates does not decide.',
  },
  grants_roles: {
    Icon: Users,
    title: 'This makes the role exclusive',
    body:
      'A role that can add and remove people can give a colleague any role '
      + 'and be given one back. So anybody holding this may hold no other '
      + 'role on the same case — exactly how the case owner works today.',
  },
  discloses: {
    Icon: Send,
    title: 'This decides what the subject of a case sees',
    body:
      'Holders choose which allegations are put to the person the case is '
      + 'about, and what evidence they are sent.',
  },
}

export default function RoleBuilder() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null) // role | 'new' | null

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    return caseRoles()
      .then((res) => setData(res === NOT_ENTITLED ? { roles: [], capabilities: [] } : res))
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const roles = data?.roles || []
  const vocabulary = data?.capabilities || []

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <Notice tone="accent" icon={ShieldCheck} title="Names are yours. Powers are ours to publish, yours to combine.">
          These are the roles somebody can hold on a case. You can rename any
          of them, build your own from the list of things Kwery can enforce,
          and retire ones you do not use.{' '}
          <strong className="font-semibold text-ink">
            You cannot invent a new power
          </strong>{' '}
          — every item on that list is a gate written into the product, and a
          tick box with nothing behind it would look like permission and be
          none. Four roles have fixed powers because they carry the
          separations a fair procedure depends on; you can still rename them.
        </Notice>
      </div>

      {error && (
        <div className="mb-4">
          <Refusal error={error} onDismiss={() => setError(null)} />
        </div>
      )}

      <div className="mb-4 flex justify-end">
        <Button variant="primary" icon={Plus} onClick={() => setEditing('new')}>
          Build a role
        </Button>
      </div>

      {loading && !data ? (
        <Panel>
          <Spinner label="Loading your organisation’s case roles" />
        </Panel>
      ) : (
        <div className="space-y-3">
          {roles.map((r) => (
            <RoleCard
              key={r.key}
              role={r}
              vocabulary={vocabulary}
              onEdit={() => setEditing(r)}
            />
          ))}
        </div>
      )}

      {editing && (
        <RoleEditor
          role={editing === 'new' ? null : editing}
          vocabulary={vocabulary}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
        />
      )}
    </div>
  )
}

function RoleCard({ role, vocabulary, onEdit }) {
  const held = new Set(role.capabilities)
  return (
    <Panel
      eyebrow={
        role.is_builtin
          ? `Built in · version ${role.version_no}`
          : `Your own · version ${role.version_no}`
      }
      title={role.label}
      right={
        <div className="flex items-center gap-2">
          {role.decides && <Chip tone="amber">Decides</Chip>}
          {role.investigates && <Chip tone="blue">Investigates</Chip>}
          {role.owner_exclusive && <Chip tone="amber">Exclusive</Chip>}
          {!role.is_active && <Chip>Retired</Chip>}
          <Button size="sm" onClick={onEdit}>
            Edit
          </Button>
        </div>
      }
    >
      {/* THE WHOLE VOCABULARY, WITH WHAT IS HELD MARKED. Not a list of
          the capabilities this role has — the absences are half the
          definition and the half somebody checking a role is looking
          for. */}
      <div className="flex flex-wrap gap-1.5">
        {vocabulary
          .filter((c) => !c.always_on)
          .map((c) => (
            <span
              key={c.capability}
              title={c.description}
              className={`chip ${held.has(c.capability) ? 'chip-accent' : ''}`}
              style={
                held.has(c.capability)
                  ? undefined
                  : { opacity: 0.4, textDecoration: 'line-through' }
              }
            >
              {c.name}
            </span>
          ))}
      </div>

      <p className="mt-3 text-[11.5px] leading-relaxed text-faint">
        Recorded on a case as <span className="mono text-muted">{role.key}</span>{' '}
        — permanent.{' '}
        {role.live_grants > 0
          ? `${pluralise(role.live_grants, 'person holds', 'people hold')} it right now.`
          : 'Nobody holds it right now.'}
        {role.is_protected && ' Its powers are fixed by Kwery.'}
      </p>
    </Panel>
  )
}

// ======================================================================
// THE EDITOR
// ======================================================================

function RoleEditor({ role, vocabulary, onClose, onSaved }) {
  const isNew = !role
  const protectedRole = !!role?.is_protected

  const [label, setLabel] = useState(role?.label || '')
  const [caps, setCaps] = useState(new Set(role?.capabilities || ['view_case']))
  const [active, setActive] = useState(role?.is_active ?? true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const [prompt, setPrompt] = useState(false)
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState(null)

  const toggle = (cap) => {
    if (protectedRole) return
    setCaps((prev) => {
      const next = new Set(prev)
      if (next.has(cap)) next.delete(cap)
      else next.add(cap)
      return next
    })
  }

  const chosen = [...caps]
  const capsChanged =
    !isNew
    && JSON.stringify([...caps].sort())
      !== JSON.stringify([...(role.capabilities || [])].sort())

  const save = async () => {
    setError(null)
    if (isNew) {
      setBusy(true)
      try {
        await createCaseRole({ label: label.trim(), capabilities: chosen })
        onSaved()
      } catch (e) {
        setError(e)
      } finally {
        setBusy(false)
      }
      return
    }

    // A RENAME OR A RETIREMENT NEEDS NO PROMPT. Nothing about what
    // anybody can see changes, so asking "apply to open cases?" would be
    // asking about a change that has no effect on them — which trains
    // people to click through the prompt that does.
    if (!capsChanged) {
      setBusy(true)
      try {
        await updateCaseRole(role.key, {
          label: label.trim(),
          is_active: active,
          apply_to_open_cases: false,
        })
        onSaved()
      } catch (e) {
        setError(e)
      } finally {
        setBusy(false)
      }
      return
    }

    setPrompt(true)
    setPreview(null)
    setPreviewError(null)
    try {
      setPreview(await previewCaseRole(role.key, chosen))
    } catch (e) {
      setPreviewError(e)
    }
  }

  const confirm = async (applyToOpenCases) => {
    setBusy(true)
    setError(null)
    try {
      await updateCaseRole(role.key, {
        label: label.trim(),
        capabilities: chosen,
        is_active: active,
        apply_to_open_cases: applyToOpenCases,
      })
      setPrompt(false)
      onSaved()
    } catch (e) {
      setPrompt(false)
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Modal
        open={!prompt}
        onClose={busy ? () => {} : onClose}
        width="max-w-3xl"
        title={isNew ? 'Build a case role' : `Edit ${role.label}`}
        subtitle={
          isNew
            ? 'Name it, then choose what somebody holding it can do on a case.'
            : protectedRole
              ? 'You can rename this role and retire it. Its powers are fixed.'
              : `Saving a change to its powers writes version ${role.version_no + 1}. Everybody who already holds it keeps version ${role.version_no} until you say otherwise.`
        }
      >
        {error && (
          <div className="mb-4">
            <Refusal error={error} onDismiss={() => setError(null)} />
          </div>
        )}

        <div className="space-y-5">
          <Field
            label="Name"
            required
            hint="What everybody granting or reading access to a case sees. Safe to change at any time — cases record which role somebody holds, not what it is called."
          >
            <input
              className="field"
              maxLength={40}
              value={label}
              disabled={busy}
              placeholder="Hearing panel member"
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>

          {protectedRole && (
            <Notice tone="amber" icon={Lock} title="These powers are fixed">
              {role.key === 'investigator'
                && 'An investigator gathers the evidence and writes up the interviews, and deliberately cannot decide the case. The person who gathered the evidence is the worst person to rule on whether it is persuasive.'}
              {role.key === 'decision_maker'
                && 'A decision maker rules on the allegations and deliberately cannot write up the interviews they decide from. Whoever decides does not author the record.'}
              {role.key === 'case_owner'
                && 'A case owner runs the procedure — scheduling, chasing, disclosing, granting access — and deliberately cannot decide it. Whoever administers does not adjudicate.'}
              {role.key === 'appeal_manager'
                && 'An appeal manager is defined as the decision maker plus the appeal outcome and the bundle. Editing it separately would let the two drift apart, and whoever decided the case must not decide the appeal.'}
              {' '}
              Build a role of your own if you need a different combination.
            </Notice>
          )}

          <div className="border-t border-line pt-4">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <span className="eyebrow">What it can do</span>
              <span className="text-[11.5px] text-faint">
                Everything not ticked is something this role cannot do.
              </span>
            </div>
            <ul className="space-y-1.5">
              {vocabulary.map((c) => (
                <CapabilityRow
                  key={c.capability}
                  option={c}
                  checked={caps.has(c.capability) || c.always_on}
                  disabled={busy || protectedRole || !c.selectable || c.always_on}
                  readOnly={protectedRole}
                  onToggle={() => toggle(c.capability)}
                />
              ))}
            </ul>
          </div>

          {!isNew && (
            <div className="border-t border-line pt-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!active}
                  disabled={busy}
                  onChange={(e) => setActive(!e.target.checked)}
                />
                <span>
                  <span className="block font-head text-[12.5px] font-semibold text-ink">
                    Retire this role
                  </span>
                  {/* WHY THERE IS NO DELETE BUTTON, said where somebody
                      would look for one. */}
                  <span className="mt-0.5 block text-[12px] leading-relaxed text-faint">
                    It stops being offered when somebody is given access to a
                    case. Everybody who already holds it keeps exactly what
                    they have — a role is never deleted, because the record
                    that somebody had access to a case is the one thing that
                    must survive.
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>

        <footer className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={!label.trim() || caps.size === 0}
            onClick={save}
          >
            {isNew ? 'Create role' : 'Save'}
          </Button>
        </footer>
      </Modal>

      <RoleMigrationPrompt
        open={prompt}
        onClose={() => {
          setPrompt(false)
          setPreview(null)
          setPreviewError(null)
        }}
        roleLabel={role?.label}
        nextVersionNo={(role?.version_no ?? 0) + 1}
        preview={preview}
        previewError={previewError}
        busy={busy}
        onConfirm={confirm}
      />
    </>
  )
}

/** One capability, with its consequence stated AT THE POINT OF
 *  SELECTION rather than after the fact.
 *
 *  The three that carry consequences are marked by the server, not
 *  here — acl publishes the flag and this renders a specific warning per
 *  flag. A warning shown only after saving is a warning about something
 *  that already happened. */
function CapabilityRow({ option, checked, disabled, readOnly, onToggle }) {
  const warn = checked && !readOnly && CONSEQUENCE[option.consequence]
  return (
    <li
      className={`rounded-md border px-3.5 py-2.5 ${
        checked ? 'border-accent-line bg-accent-dim' : 'border-line bg-surface-2'
      }`}
    >
      <label
        className={`flex items-start gap-3 ${
          disabled ? '' : 'cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          className="mt-1 shrink-0"
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-head text-[12.5px] font-semibold text-ink">
              {option.name}
            </span>
            {option.always_on && <Chip>always on</Chip>}
            {option.consequence === 'adjudicates' && (
              <Chip tone="amber">deciding</Chip>
            )}
            {option.consequence === 'grants_roles' && (
              <Chip tone="amber">exclusive</Chip>
            )}
            {!option.selectable && !option.always_on && (
              <Chip tone="danger">not available</Chip>
            )}
          </span>
          <span className="measure mt-0.5 block text-[12px] leading-relaxed text-muted">
            {option.description}
          </span>
          {/* WHY A CAPABILITY IS WITHHELD, next to the disabled box. A
              greyed checkbox with no reason beside it is a screen
              nobody can act on. */}
          {option.blocked_reason && (
            <span className="measure mt-1.5 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-faint">
              <Ban size={12} strokeWidth={1.9} className="mt-0.5 shrink-0" />
              {option.blocked_reason}
            </span>
          )}
        </span>
      </label>

      {warn && (
        <div className="mt-2.5 flex items-start gap-2.5 rounded-md border border-amber-line bg-amber-dim px-3 py-2">
          <warn.Icon
            size={14}
            strokeWidth={1.9}
            className="mt-0.5 shrink-0 text-amber"
          />
          <div className="min-w-0">
            <p className="font-head text-[11.5px] font-semibold text-amber">
              {warn.title}
            </p>
            <p className="measure mt-0.5 text-[11.5px] leading-relaxed text-muted">
              {warn.body}
            </p>
          </div>
        </div>
      )}
    </li>
  )
}
